import { useCallback, useEffect, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import type { RootState } from '../../app/store';
import { TASKROUTER_WSLINK } from '../../constants';
import { MenuStages } from '../../constants/enums';
import { selectAssistCard } from '../../redux/features/assistRequest/assistRequest.selectors';
import {
  assistCardDeadlinePassed,
  assistPoolCleared,
  isAssistCardActive,
} from '../../redux/features/assistRequest/assistRequest.slice';
import {
  selectConfig,
  selectTaskRouterNotificationSound,
  selectTRAgentAutoAssignment,
} from '../../redux/features/config/config.selector';
import {
  selectAgentStatus,
  selectCurrentTask,
  selectWSConnectionMessage,
} from '../../redux/features/taskRouter/taskRouter.selector';
import { AgentStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import { selectUserProfile } from '../../selectors/user';
import { ASSIST_CARD_AUDIO } from '../../sounds/assist_card_audio';
import { warmEventApi } from '../../utils/assistRequest/answerTransport';
import {
  ASSIST_AUDIO_CLIP_EVENT,
  assistClipStore,
  parseAssistClip,
  playAssistClip,
} from '../../utils/assistRequest/hearAndConfirm/clip';
import { routeAssistPoolEvent } from '../../utils/assistRequest/poolEvents';
import { assistCardLogFields } from '../../utils/assistRequest/logFields';
import { assistPlaybackRate } from '../../utils/assistRequest/types';
import logger from '../../utils/logger';
import { getWebsocketUrl } from '../../utils/network';

/**
 * How long a cue's `ended` is waited for (~270 ms cue) before its listener is
 * dropped, when the browser never reports it (blocked, or no event).
 */
export const ASSIST_CUE_FALLBACK_MS = 300;

/** A card's arrival cue: `done` once it ended; `stop` cuts it short. */
type AssistCue = { done: Promise<void>; stop: () => void };

const NO_CUE: AssistCue = { done: Promise.resolve(), stop: () => {} };

/**
 * Plays the arrival cue; `done` resolves when it ended, failed, was stopped,
 * or after `ASSIST_CUE_FALLBACK_MS`, whichever comes first. No cue when sound
 * is off.
 */
const playCue = (canPlaySound: boolean): AssistCue => {
  const cue = ASSIST_CARD_AUDIO;
  if (!canPlaySound || !cue) return NO_CUE;
  let finish = () => {};
  const done = new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    finish = () => {
      if (timer === undefined) return;
      clearTimeout(timer);
      timer = undefined;
      cue.removeEventListener?.('ended', finish);
      resolve();
    };
    timer = setTimeout(finish, ASSIST_CUE_FALLBACK_MS);
    cue.addEventListener?.('ended', finish);
    // A browser that blocks the sound must not break the card.
    try {
      cue.play()?.catch(finish);
    } catch {
      finish();
    }
  });
  const stop = () => {
    try {
      cue.pause?.();
      cue.currentTime = 0;
    } catch {
      // A cue that cannot be stopped just ends on its own.
    }
    finish();
  };
  return { done, stop };
};

/**
 * HITL-1792 (HITL 8.0.0): the lobby's assist pool feed.
 *
 * Joins the console's existing `TASK_CHANNEL_<user id>` socket — the same URL
 * `useTaskRouterWS` opens in auto-assignment mode, with `share: true`, so no
 * second connection is made and `useTaskRouterWS` is untouched. Its
 * `processTaskRouterMessage` ignores events without a `task_id`, so the two
 * subscribers never act on each other's events.
 *
 * Receive only: task-router assigns a request straight to this agent, the
 * card shows at once, and the answer is posted over REST (`useAssistAnswer`).
 * The card's local deadline is kept here too. The guest's audio arrives on the
 * same socket, pushed by live-transcriber (`assist_audio_clip`, HITL-1786):
 * it is held outside Redux for the live card only, and plays on its own the
 * moment both are there (the AI holds its reply for the answer, so every
 * 100 ms counts). The clip is the alert: the arrival cue plays only while the
 * clip has not come yet, and a clip that lands during it cuts it short. Chrome lets it start once the page has had any click.
 * Nothing is announced: task-router infers lobby presence from the existing
 * `agent_status_available` heartbeat, the assigned task and heartbeat expiry
 * (contract D5). The pool runs only in auto-assignment mode, where the channel
 * is per agent.
 *
 * Not gated on `assist_requests`: that flag is evaluated per store, and the
 * lobby has no store (its Unleash context keeps the last session's restaurant
 * code, or none after login). task-router checks the flag with the request's
 * store at ingest, so a request only ever arrives for a flag-on store; with
 * the flag off everywhere this hook receives nothing and renders nothing.
 *
 * Reconnection is left to `useTaskRouterWS`. react-use-websocket runs each
 * shared subscriber's own `shouldReconnect` on a close, and any subscriber that
 * reconnects reopens the socket for all of them — so a pool subscriber with
 * its own rules would override the order socket's (no reconnect after a 1005
 * close, its retry budget, `onReconnectStop`). This subscriber stays
 * registered on the URL across a drop, so it receives again as soon as
 * `useTaskRouterWS` has reconnected.
 *
 * Messages are handled in `onMessage` only; `filter: () => false` keeps
 * `lastMessage` unset, so a task-channel message (an audio clip is 0.1–1 MB)
 * never re-renders the lobby through this hook.
 */
const useAssistPool = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const config = useAppSelector(selectConfig);
  const userId = useAppSelector(selectUserProfile)?.id;
  const isTaskAssignmentMode = useAppSelector(selectTRAgentAutoAssignment);
  const canPlaySound = useAppSelector(selectTaskRouterNotificationSound);
  const currentTaskId = useAppSelector(selectCurrentTask);
  const card = useAppSelector(selectAssistCard);
  const isActive = Boolean(isTaskAssignmentMode && userId);
  // Why no card may show now, or null. The task socket's alert is up
  // (dropped, retrying or given up): the lobby shows the alert instead of the
  // panel, so nothing may run behind it (review B1.1). The agent is not
  // Available (Unavailable, Training): task-router assigns Available agents
  // only, but a status change and an assignment can cross (review B1.7).
  const isSocketDown = Boolean(useAppSelector(selectWSConnectionMessage));
  const agentStatus = useAppSelector(selectAgentStatus);
  let pauseReason: string | null = null;
  if (isSocketDown) pauseReason = 'socket_down';
  else if (agentStatus !== AgentStatuses.available)
    pauseReason = `status_${agentStatus}`;
  const pauseReasonRef = useRef(pauseReason);
  pauseReasonRef.current = pauseReason;

  const hasCurrentTaskRef = useRef(Boolean(currentTaskId));
  hasCurrentTaskRef.current = Boolean(currentTaskId);
  const canPlaySoundRef = useRef(canPlaySound);
  canPlaySoundRef.current = canPlaySound;
  const eventApiRef = useRef(config.EVENT_API);
  eventApiRef.current = config.EVENT_API;
  /** The live card's arrival cue, cut short when its clip starts. */
  const cueRef = useRef<({ requestId: string } & AssistCue) | null>(null);

  /**
   * Starts the live card's clip on its own, once per card, cutting its cue
   * short.
   * Called when the card shows (a clip already held) and when a clip arrives
   * for the live card; before the card's cue has started there is nothing to
   * do yet: the card-shown effect calls it again.
   */
  const autoplayClip = (requestId: string) => {
    const cue = cueRef.current;
    if (cue?.requestId !== requestId) return;
    if (!assistClipStore.get(requestId)) return;
    if (!assistClipStore.claimAutoplay(requestId)) return;
    cue.stop();
    void cue.done.then(() => {
      // The card may have ended (answer, deadline, order) meanwhile.
      const liveCard = store.getState().assistRequest.card;
      const clip = assistClipStore.get(requestId);
      if (
        !clip ||
        !isAssistCardActive(liveCard) ||
        liveCard.requestId !== requestId
      ) {
        return;
      }
      void playAssistClip(
        clip,
        assistPlaybackRate(liveCard.request?.playback_rate),
        { auto: true, logFields: assistCardLogFields(liveCard) }
      );
    });
  };

  // A clip is for the live card, or for a card that has not shown yet (the
  // two arrive on different hops, in either order). One that lands after its
  // card ended (answered, deadline, order) has no one to play it.
  const receiveClip = (message: any) => {
    const route = parseAssistClip(message);
    const requestId = message?.data?.request_id;
    if ('ignored' in route) {
      logger.debug({
        message: 'Assist clip ignored',
        reason: route.ignored,
        requestId,
        isTR: true,
      });
      return;
    }
    const { card: liveCard, endedIds } = store.getState().assistRequest;
    if (
      isAssistCardActive(liveCard) &&
      liveCard.requestId === route.clip.requestId
    ) {
      const at = Date.now();
      assistClipStore.put(route.clip, at);
      logger.info({
        message: 'Assist clip received',
        ...assistCardLogFields(liveCard),
        durationMs: route.clip.durationMs,
        msAfterCard: at - liveCard.receivedAt,
      });
      autoplayClip(liveCard.requestId);
      return;
    }
    const hasEnded =
      endedIds.includes(route.clip.requestId) ||
      liveCard?.requestId === route.clip.requestId;
    if (hasEnded) {
      logger.debug({
        message: 'Assist clip ignored',
        reason: 'not_live_card',
        requestId,
        isTR: true,
      });
      return;
    }
    assistClipStore.holdEarly(route.clip, Date.now());
    logger.debug({
      message: 'Assist clip held for its card',
      requestId,
      isTR: true,
    });
  };

  // A card that just showed takes the clip that arrived before it. It starts
  // on its own once the card's cue is done (the card-shown effect below).
  const adoptEarlyClip = () => {
    const liveCard = store.getState().assistRequest.card;
    if (!isAssistCardActive(liveCard)) return;
    const adopted = assistClipStore.adoptEarly(liveCard.requestId, Date.now());
    if (!adopted) return;
    logger.info({
      message: 'Assist clip received',
      ...assistCardLogFields(liveCard),
      durationMs: adopted.clip.durationMs,
      msAfterCard: -adopted.waitedMs,
    });
  };

  useWebSocket(
    isActive
      ? getWebsocketUrl(
          config,
          `${TASKROUTER_WSLINK}_${userId}`,
          MenuStages.LIVE
        )
      : null,
    {
      share: true,
      // Reconnection belongs to `useTaskRouterWS` (see above).
      shouldReconnect: () => false,
      // Handled in onMessage; never stored, so never a re-render.
      filter: () => false,
      onMessage: (message: MessageEvent) => {
        let parsed;
        try {
          parsed = JSON.parse(message.data);
        } catch {
          return;
        }
        if (parsed?.event === ASSIST_AUDIO_CLIP_EVENT) {
          receiveClip(parsed);
          return;
        }
        const route = routeAssistPoolEvent(parsed, Date.now(), {
          hasCurrentTask: hasCurrentTaskRef.current,
        });
        if ('action' in route && pauseReasonRef.current) {
          logger.info({
            message: 'Assist card dropped: pool paused',
            reason: pauseReasonRef.current,
            requestId: parsed?.data?.request_id,
            isTR: true,
          });
          return;
        }
        if ('action' in route) {
          dispatch(route.action);
          adoptEarlyClip();
          return;
        }
        if (route.ignored === 'not_pool_event') return;
        if (route.ignored === 'dropped_too_late') {
          logger.info({
            message: 'Assist card dropped_too_late',
            requestId: parsed?.data?.request_id,
            remaining_ms: parsed?.data?.remaining_ms,
            isTR: true,
          });
          return;
        }
        const log =
          route.ignored === 'unsupported_schema' ? logger.warn : logger.debug;
        log({
          message: 'Assist pool event ignored',
          reason: route.ignored,
          event: parsed?.event,
          requestId: parsed?.data?.request_id,
          schemaVersion: parsed?.data?.schema_version,
          isTR: true,
        });
      },
    }
  );

  // The agent has a second or two to act: a short cue of its own says a card
  // is up, once per card. It is not the order-assignment sound and plays on its
  // own Audio element, so a card never cuts an order's sound short (or the
  // reverse). Gated by the same notification-sound setting as orders. A card
  // whose clip is already held skips the cue: the guest's voice is the alert,
  // ~270 ms sooner. The event API connection is warmed for the answer.
  const liveRequestId = isAssistCardActive(card) ? card.requestId : undefined;
  const liveDeadlineAt = isAssistCardActive(card) ? card.deadlineAt : undefined;
  useEffect(() => {
    if (!liveRequestId) return;
    const shown = store.getState().assistRequest.card;
    if (isAssistCardActive(shown)) {
      const now = Date.now();
      logger.info({
        message: 'Assist card shown',
        ...assistCardLogFields(shown),
        receiptToShownMs: now - shown.receivedAt,
        timeLeftMs: shown.deadlineAt - now,
      });
    }
    warmEventApi(eventApiRef.current);
    cueRef.current = {
      requestId: liveRequestId,
      ...(assistClipStore.get(liveRequestId)
        ? NO_CUE
        : playCue(canPlaySoundRef.current)),
    };
    autoplayClip(liveRequestId);
    // autoplayClip reads refs and the store only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveRequestId]);

  // At the card's local deadline an unanswered card ends and nothing is sent:
  // the request went to several agents, and who answered is in the data.
  useEffect(() => {
    if (!liveRequestId || liveDeadlineAt === undefined) return undefined;
    const timer = setTimeout(() => {
      const expiring = store.getState().assistRequest.card;
      if (
        isAssistCardActive(expiring) &&
        expiring.requestId === liveRequestId
      ) {
        logger.info({
          message: 'Assist card expired',
          ...assistCardLogFields(expiring),
          shownMs: Date.now() - expiring.receivedAt,
          ...assistClipStore.stats(liveRequestId),
        });
      }
      dispatch(
        assistCardDeadlinePassed({ requestId: liveRequestId, at: Date.now() })
      );
    }, Math.max(0, liveDeadlineAt - Date.now()));
    return () => clearTimeout(timer);
  }, [dispatch, liveDeadlineAt, liveRequestId, store]);

  /**
   * Clears the pool only when it holds something: with no pool events the
   * slice must see no actions (FND-19), and a clear always makes new state.
   */
  const clearPool = useCallback(
    (reason: string) => {
      const held = store.getState().assistRequest.card;
      if (!held) return;
      if (isAssistCardActive(held)) {
        logger.info({
          message: 'Assist card dropped',
          ...assistCardLogFields(held),
          reason,
          shownMs: Date.now() - held.receivedAt,
        });
      }
      dispatch(assistPoolCleared({ reason, at: Date.now() }));
    },
    [dispatch, store]
  );

  // An order offer always beats an assist card: drop it the moment a task is
  // current. task-router takes the assignment back itself (`order_offer`).
  useEffect(() => {
    if (isActive && currentTaskId) clearPool('order_offer');
  }, [clearPool, isActive, currentTaskId]);

  // Paused (socket alert up, or the agent left Available): a live card would
  // run its cue, clip and deadline behind the alert or for an agent who
  // stepped away. It ends unanswered.
  useEffect(() => {
    if (isActive && pauseReason) clearPool(pauseReason);
  }, [clearPool, isActive, pauseReason]);

  // Leaving the lobby, or auto-assignment turning off. Nothing is sent: an
  // unanswered card is simply one answer fewer, and its deadline ends it.
  useEffect(() => {
    if (!isActive) return undefined;
    return () => clearPool('pool_inactive');
  }, [clearPool, isActive]);

  return { isActive };
};

export default useAssistPool;
